import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Dialog component to warn users about unsaved changes
 *
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {Function} onConfirm - Callback when user confirms leaving
 * @param {Function} onCancel - Callback when user cancels (stays on page)
 * @param {string} message - Custom message to display
 */
const UnsavedChangesDialog = ({
  isOpen,
  onConfirm,
  onCancel,
  message = 'You have unsaved changes. Are you sure you want to leave?'
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="unsaved-changes-dialog"
    >
      <div
        className="glass border border-light-border dark:border-border rounded-2xl p-6 max-w-md w-full animate-in fade-in zoom-in-95 duration-200"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="unsaved-dialog-title"
        aria-describedby="unsaved-dialog-description"
      >
        {/* Warning Icon */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-yellow-500/20 rounded-xl">
            <AlertTriangle className="w-6 h-6 text-yellow-400" />
          </div>
          <h2
            id="unsaved-dialog-title"
            className="font-display text-xl italic tracking-tighter text-light-text dark:text-pure-white"
          >
            Unsaved Changes
          </h2>
        </div>

        {/* Message */}
        <p
          id="unsaved-dialog-description"
          className="font-body text-light-text-secondary dark:text-silver/80 mb-6"
        >
          {message}
        </p>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 px-4 border border-light-border dark:border-border rounded-xl font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver hover:border-white/20 hover:bg-silver/5 transition-smooth"
            data-testid="unsaved-changes-stay"
          >
            Stay
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-3 px-4 bg-red-500/20 border border-red-500/30 rounded-xl font-mono text-sm uppercase tracking-widest text-red-400 hover:bg-red-500/30 transition-smooth"
            data-testid="unsaved-changes-leave"
          >
            Leave
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnsavedChangesDialog;
